import { Children, memo, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import SelfieCenter from 'verify/components/SelfiePictograph/SelfieCenter';
import SelfieLeft1 from 'verify/components/SelfiePictograph/SelfieLeft1';
import SelfieLeft2 from 'verify/components/SelfiePictograph/SelfieLeft2';
import SelfieLeft3 from 'verify/components/SelfiePictograph/SelfieLeft3';
import SelfieRight1 from 'verify/components/SelfiePictograph/SelfieRight1';
import SelfieRight2 from 'verify/components/SelfiePictograph/SelfieRight2';
import SelfieRight3 from 'verify/components/SelfiePictograph/SelfieRight3';

interface SelfiePictographProps {
  children: ReactNode;
  // in milliseconds
  frameDuration?: number;
}

const SelfiePictograph = ({ children, frameDuration }: SelfiePictographProps) => {
  if (Children.count(children) > 1) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return <Slides frameDuration={frameDuration}>{children}</Slides>;
  } else {
    return <>{children}</>;
  }
};

SelfiePictograph.Center = SelfieCenter;
SelfiePictograph.Left1 = SelfieLeft1;
SelfiePictograph.Left2 = SelfieLeft2;
SelfiePictograph.Left3 = SelfieLeft3;
SelfiePictograph.Right1 = SelfieRight1;
SelfiePictograph.Right2 = SelfieRight2;
SelfiePictograph.Right3 = SelfieRight3;

export default SelfiePictograph;

const SlidesContainer = styled.div`
  position: relative;
`;

interface SlidesProps {
  children: ReactNode;
  frameDuration: number;
}

const Slides = memo(
  function SlidesInner({ children, frameDuration }: SlidesProps) {
    // Array where index is current animation frame and value is index of child to show.
    // Something like [1, 2, 2, 2, 2, 3, 4, 4, 4, 4, 5]
    // It's not recommended to pull props from children when mapping over,
    // but it makes the API a lot cleaner here.
    // useRef performs better than useMemo when dependency array is empty.
    const frames = useRef<number[]>(
      Children.toArray(children).flatMap((child, idx) =>
        new Array((child as any).props.frames ?? 1).fill(idx),
      ),
    );

    const [activeFrame, setActiveFrame] = useState(0);

    // We use setTimeout instead of setInterval because every state update will
    // retrigger the effect
    useEffect(() => {
      const timeout = setTimeout(
        () => setActiveFrame((activeFrame) => (activeFrame + 1) % frames.current.length),
        frameDuration,
      );
      return () => clearTimeout(timeout);
    });

    return (
      <SlidesContainer>{Children.toArray(children)[frames.current[activeFrame]]}</SlidesContainer>
    );
  },
  // Never rerender for performance. This doesn't need to be dynamic anyways.
  () => true,
);
